/**
 * Public Checkout Page Component.
 * Allows anonymous users to start a subscription checkout process.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { StripeEmbeddedCheckout } from '@/components/StripeEmbeddedCheckout';
import { PlanSelection } from '@/components/PlanSelection';
import {
  CheckoutLayout,
  CheckoutCard,
  CheckoutFeaturesList,
  CheckoutSecurityBadge,
  CheckoutFooter,
} from '@/components/checkout';

export const PublicCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'plan' | 'email' | 'checkout'>('plan');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('monthly');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handlePlanSelect = (planId: string) => {
    setSelectedPlanId(planId);
    setError(null);
  };

  const handlePlanContinue = () => {
    if (selectedPlanId) {
      setStep('email');
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setError(null);
    setStep('checkout');
  };

  const handleBackToPlan = () => {
    setStep('plan');
  };

  const handleBackToEmail = () => {
    setStep('email');
  };

  const handleSuccess = () => {
    // Redirect to success page with email parameter
    navigate(`/checkout/success?email=${encodeURIComponent(email)}`);
  };

  const handleError = (error: string) => {
    setError(error);
    setStep('email');
  };

  return (
    <CheckoutLayout
      title="Get Started Today"
      subtitle="Start your premium subscription and unlock unlimited access to all AI companions."
    >
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Features List */}
        <div className="space-y-6">
          <CheckoutFeaturesList />
          <CheckoutSecurityBadge />
        </div>

        {/* Checkout Form */}
        <div className="lg:sticky lg:top-8">
          {step === 'plan' && (
            <CheckoutCard>
              <PlanSelection
                selectedPlanId={selectedPlanId}
                onPlanSelect={handlePlanSelect}
                onContinue={handlePlanContinue}
              />
            </CheckoutCard>
          )}

          {step === 'email' && (
            <CheckoutCard>
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={handleBackToPlan}
                  className="text-kind-text-secondary hover:text-kind-text-primary transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h2 className="text-2xl font-bold text-kind-text-primary">Enter Your Email</h2>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-kind-text-primary mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-kind-text-secondary" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full pl-10 pr-4 py-3 bg-kind-deep border border-gray-300 dark:border-white/10 rounded-lg text-kind-text-primary placeholder-kind-text-secondary focus:outline-none focus:ring-2 focus:ring-kind-purple focus:border-kind-purple"
                      required
                    />
                  </div>
                  <p className="text-xs text-kind-text-secondary mt-2">
                    We'll use this email for your account and subscription details
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors"
                >
                  <Lock className="h-5 w-5" />
                  Continue to Payment
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-300 dark:border-white/10">
                <p className="text-xs text-kind-text-secondary text-center">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </CheckoutCard>
          )}

          {step === 'checkout' && (
            <CheckoutCard>
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={handleBackToEmail}
                  className="text-kind-text-secondary hover:text-kind-text-primary transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <h2 className="text-2xl font-bold text-kind-text-primary">Complete Your Payment</h2>
              </div>
              <p className="text-kind-text-secondary mb-6">
                You're almost there! Complete your payment to start your subscription.
              </p>

              <StripeEmbeddedCheckout
                isAnonymous={true}
                userEmail={email}
                selectedPlanId={selectedPlanId}
                showPlanSelection={false}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </CheckoutCard>
          )}
        </div>
      </div>

      <CheckoutFooter />
    </CheckoutLayout>
  );
};

export default PublicCheckoutPage;
